import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { UserData } from '../Models/employee-and-user-data';
import { TripLeg } from '../Models/trip-leg';
import { TripContext, TripPhase } from '../Models/trip-context';
import { legVoyageCode } from '../utils/voyage-pms-code';

const END_OF_CRUISE_CUTOFF = '8:00:00';
const EARLY_ACCESS_DAYS = 14;
const TIME_FORMAT = 'HH:mm:ss';

/** Extract a date-only moment (midnight) from a Date or string. */
function toDay(d: Date | string | undefined): moment.Moment {
  if (d == null) return moment.invalid();
  return moment(moment(d).format(moment.HTML5_FMT.DATE));
}

/** True if the cruise-end-day grace period has passed (after 8 AM on end date). */
function isPastEndDayCutoff(
  cruiseEnd: Date | string | undefined,
  nowDate: string,
  nowTime: string
): boolean {
  const endDay = toDay(cruiseEnd);
  if (!endDay.isValid()) return false;
  if (endDay.isSame(nowDate)) {
    return !moment(nowTime, TIME_FORMAT).isBefore(
      moment(END_OF_CRUISE_CUTOFF, TIME_FORMAT)
    );
  }
  return moment(nowDate).isAfter(endDay);
}

/**
 * True when "now" falls inside a leg's active sailing window.
 * Matches existing pagemask === false logic:
 *   evtBegDate <= today AND (evtEndDate > today OR (evtEndDate == today AND time < 8am))
 */
function isOnLeg(
  leg: TripLeg,
  nowDate: string,
  nowTime: string
): boolean {
  const begDay = toDay(leg.evtBegDate);
  const endDay = toDay(leg.evtEndDate);
  if (!begDay.isValid() || !endDay.isValid()) return false;

  const todayIsOnOrAfterBeg = begDay.isSameOrBefore(nowDate);
  const todayIsBeforeEnd = endDay.isAfter(nowDate);
  const todayIsEndDayBeforeCutoff =
    endDay.isSame(nowDate) &&
    moment(nowTime, TIME_FORMAT).isBefore(
      moment(END_OF_CRUISE_CUTOFF, TIME_FORMAT)
    );

  return todayIsOnOrAfterBeg && (todayIsBeforeEnd || todayIsEndDayBeforeCutoff);
}

/**
 * True when the next leg starts exactly one calendar day after the previous leg ends.
 * PM rule: no gap preview when back-to-back.
 */
function isBackToBack(prevLeg: TripLeg, nextLeg: TripLeg): boolean {
  const prevEnd = toDay(prevLeg.evtEndDate);
  const nextBeg = toDay(nextLeg.evtBegDate);
  if (!prevEnd.isValid() || !nextBeg.isValid()) return false;
  return nextBeg.diff(prevEnd, 'days') === 1;
}

function buildContext(
  phase: TripPhase,
  leg: TripLeg | null,
  legIndex: number
): TripContext {
  const pagemask =
    phase === 'on_leg' ? false : phase === 'preview' ? true : undefined;

  return {
    phase,
    activeLeg: leg,
    activeLegIndex: legIndex,
    itineraryMode: phase === 'on_leg' ? 'follow_calendar_day' : 'day1_only',
    pagemask,
    contentEvtCode: leg ? legVoyageCode(leg) : undefined,
    contentShipId: leg?.shipUid,
    contentShipName: leg?.shipName,
  };
}

/**
 * Pure resolver — given UserData and the current moment, returns a TripContext.
 *
 * Single-leg (no legs array): produces the same result as today's pagemask logic.
 * Multi-leg: walks the legs in chronological order and applies gap / back-to-back rules.
 */
export function resolveTripContext(
  userdata: UserData | undefined,
  now?: moment.Moment
): TripContext {
  const m = now ?? moment();
  const nowDate = m.format(moment.HTML5_FMT.DATE);
  const nowTime = m.format(moment.HTML5_FMT.TIME_SECONDS);

  if (!userdata) {
    return buildContext('pre_package', null, -1);
  }

  const legs = buildSortedLegs(userdata);
  if (legs.length === 0) {
    return buildContext('pre_package', null, -1);
  }

  const packageStart = userdata.packagePckgBeg ?? userdata.pckgBeg ?? legs[0].evtBegDate;
  const packageEnd = userdata.packageCruiseEnd ?? userdata.cruiseEnd ?? legs[legs.length - 1].evtEndDate;
  const earlyAccessOpens = toDay(packageStart).subtract(EARLY_ACCESS_DAYS, 'days');

  if (moment(nowDate).isBefore(earlyAccessOpens)) {
    return buildContext('pre_package', legs[0], 0);
  }

  if (isPastEndDayCutoff(packageEnd, nowDate, nowTime)) {
    return buildContext('post_package', legs[legs.length - 1], legs.length - 1);
  }

  for (let i = 0; i < legs.length; i++) {
    if (isOnLeg(legs[i], nowDate, nowTime)) {
      return buildContext('on_leg', legs[i], i);
    }
  }

  for (let i = 0; i < legs.length; i++) {
    const legBeg = toDay(legs[i].evtBegDate);
    if (!legBeg.isValid()) continue;

    if (moment(nowDate).isBefore(legBeg)) {
      if (i === 0) {
        return buildContext('preview', legs[0], 0);
      }

      const prevLeg = legs[i - 1];
      if (isBackToBack(prevLeg, legs[i])) {
        return buildContext('on_leg', legs[i], i);
      }

      return buildContext('preview', legs[i], i);
    }
  }

  return buildContext('post_package', legs[legs.length - 1], legs.length - 1);
}

function buildSortedLegs(userdata: UserData): TripLeg[] {
  if (userdata.legs?.length) {
    return [...userdata.legs].sort(
      (a, b) => toDay(a.evtBegDate).valueOf() - toDay(b.evtBegDate).valueOf()
    );
  }
  if (!toDay(userdata.cruiseBeg).isValid()) {
    return [];
  }
  return [
    {
      evtCode: userdata.evtcode,
      shipUid: userdata.shipid,
      shipName: userdata.ShipName,
      evtBegDate: userdata.cruiseBeg,
      evtEndDate: userdata.cruiseEnd,
    },
  ];
}

@Injectable({
  providedIn: 'root',
})
export class TripContextService {
  resolve(userdata?: UserData, now?: moment.Moment): TripContext {
    return resolveTripContext(userdata, now);
  }

  resolveFromStorage(now?: moment.Moment): TripContext {
    const userdata: UserData | undefined = JSON.parse(
      localStorage.getItem('userinfo') || '{}'
    );
    return resolveTripContext(userdata, now);
  }
}
